import { useState, useEffect, useRef } from 'react';
import { Play, AlertTriangle, ExternalLink } from 'lucide-react';
import { SiTiktok } from 'react-icons/si';
import { videoApi } from '@/services/api';
import { parseMediaUrl } from './parseMediaUrl';
import { UploadedVideoPlayer } from './UploadedVideoPlayer';
import { Button } from '@/components/ui/Button';
import { BrandMark, BrandLoader } from '@/components/ui/brand-loader';
import { getDisplayErrorMessage } from '@/utils';

import { useTranslation } from 'react-i18next';
interface TikTokPreviewProps {
  url?: string;
  mediaUrl?: string;
  mediaKind?: 'image' | 'video';
  caption?: string;
  hashtags?: string[];
  username?: string;
  profileImageUrl?: string | null;
  boardId?: string;
  // Pre-fetched by parent (TikTok server iframe embed)
  embedUrl?: string | null;
  thumbnailUrl?: string | null;
  coverTimestampMs?: number;
  onCoverTimestampChange?: (timestampMs: number) => void;
  isLoading?: boolean;
  error?: string | null;
  /** Overrides the empty-state line, e.g. for upload-only surfaces where "Add TikTok URL" is wrong. */
  emptyLabel?: string;
}

/**
 * TikTok preview — 9:16 portrait.
 * Supports:
 *  - Server-provided iframe embedUrl (from CreateCardModal's prefetch)
 *  - On-click video extraction (CardDetailModal flow, requires boardId)
 *  - Uploaded files
 *  - Thumbnail with play button fallback
 */
export function TikTokPreview({
  url,
  mediaUrl,
  mediaKind,
  caption,
  hashtags,
  username = 'your_account',
  profileImageUrl,
  boardId,
  embedUrl: initialEmbedUrl,
  thumbnailUrl: initialThumbnail,
  coverTimestampMs = 1000,
  onCoverTimestampChange,
  isLoading,
  error: initialError,
  emptyLabel,
}: TikTokPreviewProps) {
    const { t } = useTranslation();
  const parsePreviewMedia = (nextUrl?: string, nextMediaUrl?: string) => {
    if (nextMediaUrl?.startsWith('blob:') && mediaKind) {
      return { type: mediaKind, directUrl: nextMediaUrl } as ReturnType<typeof parseMediaUrl>;
    }
    return parseMediaUrl(nextUrl, nextMediaUrl);
  };

  const [parsed, setParsed] = useState(() => parsePreviewMedia(url, mediaUrl));

  const [extractedVideoUrl, setExtractedVideoUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(initialThumbnail ?? null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(initialError ? getDisplayErrorMessage(initialError, 'Could not load preview') : null);
  const [mediaError, setMediaError] = useState(false);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
  const [durationMs, setDurationMs] = useState(60000);
  const [coverControlMs, setCoverControlMs] = useState(coverTimestampMs);
  const [frameTimestampMs, setFrameTimestampMs] = useState(coverTimestampMs);
  const scrubberRef = useRef<HTMLDivElement | null>(null);
  const isScrubbingRef = useRef(false);
  const frameTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const next = parsePreviewMedia(url, mediaUrl);
    setParsed(next);
    setExtractedVideoUrl(null);
    setThumbnailUrl(initialThumbnail ?? null);
    setIsPlaying(false);
    setVideoError(initialError ? getDisplayErrorMessage(initialError, 'Could not load preview') : null);
    setMediaError(false);
  }, [url, mediaUrl, mediaKind, initialThumbnail, initialError]);

  useEffect(() => {
    setCoverControlMs(coverTimestampMs);
    setFrameTimestampMs(coverTimestampMs);
  }, [coverTimestampMs]);

  useEffect(() => {
    return () => {
      if (frameTimerRef.current) {
        window.clearTimeout(frameTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (parsed.type !== 'video' || !parsed.directUrl) {
      setCoverPreviewUrl(null);
      return;
    }

    let cancelled = false;
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';

    const cleanup = () => {
      video.removeAttribute('src');
      video.load();
    };

    const captureFrame = () => {
      if (cancelled || !video.videoWidth || !video.videoHeight) return;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (!context) return;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        setCoverPreviewUrl(canvas.toDataURL('image/jpeg', 0.82));
      } catch {
        setCoverPreviewUrl(null);
      }
    };

    video.onloadedmetadata = () => {
      if (cancelled) return;
      const nextDurationMs = Number.isFinite(video.duration) ? Math.max(1000, Math.floor(video.duration * 1000)) : 60000;
      setDurationMs(nextDurationMs);
      video.currentTime = Math.min(Math.max(0, frameTimestampMs / 1000), Math.max(0, video.duration - 0.1));
    };
    video.onseeked = captureFrame;
    video.onerror = () => setCoverPreviewUrl(null);
    video.src = parsed.directUrl;

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [parsed.type, parsed.directUrl, frameTimestampMs]);

  const aspect = 'aspect-[9/16]';
  const base = `${aspect} w-full rounded-lg overflow-hidden relative transition-all duration-300`;

  /** TikTok-style bottom overlay */
  const Overlay = () => (
    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none z-10">
      <div className="flex items-center gap-2 mb-1.5">
        {profileImageUrl ? (
          <img src={profileImageUrl} alt={username} className="size-7 rounded-full object-cover ring-1 ring-white/30" />
        ) : (
          <div className="size-7 rounded-full bg-gray-700 border border-white/20 flex items-center justify-center text-white text-[10px] font-bold">
            {username.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="text-xs font-semibold text-white">@{username}</span>
      </div>
      {caption && <p className="text-xs text-white/90 line-clamp-2 leading-snug">{caption}</p>}
      {hashtags && hashtags.length > 0 && (
        <p className="text-xs text-cyan-300 mt-1">{hashtags.slice(0, 3).map(t => `#${t}`).join(' ')}</p>
      )}
      {/* TikTok sound bar */}
      <div className="flex items-center gap-1.5 mt-2">
        <div className="size-3 rounded-full bg-white/60" />
        <div className="flex-1 h-px bg-white/30" />
        <span className="text-[9px] text-white/50">{t('preview.originalSound')}</span>
      </div>
    </div>
  );

  const handlePlay = async () => {
    if (extractedVideoUrl) { setIsPlaying(true); return; }
    if (!boardId || !url) return;

    setIsExtracting(true);
    setVideoError(null);
    try {
      const result = await videoApi.extractVideo(url, boardId);
      if (result.video_url) {
        setExtractedVideoUrl(videoApi.getProxyUrl(result.video_url));
        if (result.thumbnail_url) setThumbnailUrl(result.thumbnail_url);
        setIsPlaying(true);
      } else {
        setVideoError(getDisplayErrorMessage(result.error, 'Could not extract video'));
      }
    } catch {
      setVideoError('Failed to extract video');
    } finally {
      setIsExtracting(false);
    }
  };

  const formatTimestamp = (milliseconds: number) => {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const commitCoverTimestamp = (timestampMs: number) => {
    const nextTimestampMs = Math.max(0, Math.min(durationMs, Math.round(timestampMs / 100) * 100));
    if (frameTimerRef.current) {
      window.clearTimeout(frameTimerRef.current);
      frameTimerRef.current = null;
    }
    setCoverControlMs(nextTimestampMs);
    setFrameTimestampMs(nextTimestampMs);
    onCoverTimestampChange?.(nextTimestampMs);
  };

  const updateCoverFromClientX = (clientX: number, commit = false) => {
    const scrubber = scrubberRef.current;
    if (!scrubber) return;
    if (isPlaying) {
      setIsPlaying(false);
    }

    const rect = scrubber.getBoundingClientRect();
    const ratio = rect.width > 0 ? Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) : 0;
    const nextTimestampMs = Math.round((ratio * durationMs) / 100) * 100;

    setCoverControlMs(nextTimestampMs);
    setFrameTimestampMs(nextTimestampMs);

    if (frameTimerRef.current) {
      window.clearTimeout(frameTimerRef.current);
    }

    if (commit) {
      commitCoverTimestamp(nextTimestampMs);
    }
  };

  const CoverSelector = () => {
    if (parsed.type !== 'video' || !parsed.directUrl || !onCoverTimestampChange) {
      return null;
    }

    return (
      <div
        className="absolute left-3 right-3 top-3 z-30 rounded-lg bg-black/65 px-3 py-2 text-white shadow-lg backdrop-blur"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
      >
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/70">{t('preview.coverFrame')}</span>
          <span className="text-[11px] font-semibold text-white">{formatTimestamp(coverControlMs)}</span>
        </div>
        <div
          ref={scrubberRef}
          role="slider"
          tabIndex={0}
          aria-label={t('preview.tiktokCoverTimestamp')}
          aria-valuemin={0}
          aria-valuemax={durationMs}
          aria-valuenow={Math.min(coverControlMs, durationMs)}
          className="relative h-7 cursor-ew-resize touch-none select-none"
          onClick={(event) => {
            event.stopPropagation();
            updateCoverFromClientX(event.clientX, true);
          }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            isScrubbingRef.current = true;
            event.currentTarget.setPointerCapture(event.pointerId);
            setIsPlaying(false);
            updateCoverFromClientX(event.clientX);
          }}
          onPointerMove={(event) => {
            if (!isScrubbingRef.current) return;
            event.preventDefault();
            event.stopPropagation();
            updateCoverFromClientX(event.clientX);
          }}
          onPointerUp={(event) => {
            if (!isScrubbingRef.current) return;
            event.preventDefault();
            event.stopPropagation();
            isScrubbingRef.current = false;
            setIsPlaying(false);
            updateCoverFromClientX(event.clientX, true);
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={(event) => {
            isScrubbingRef.current = false;
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            const direction = event.key === 'ArrowRight' ? 1 : -1;
            setIsPlaying(false);
            commitCoverTimestamp(coverControlMs + direction * 1000);
          }}
        >
          <div className="absolute left-0 right-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-white/20" />
          <div
            className="absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-cyan-400"
            style={{ width: `${durationMs > 0 ? Math.min(100, (coverControlMs / durationMs) * 100) : 0}%` }}
          />
          <div
            className="absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-cyan-400 shadow-lg"
            style={{ left: `${durationMs > 0 ? Math.min(100, (coverControlMs / durationMs) * 100) : 0}%` }}
          />
        </div>
      </div>
    );
  };

  // Loading
  if (isLoading) {
    return (
      <div className={`${base} bg-black flex items-center justify-center`}>
        <div className="flex flex-col items-center gap-3">
          <BrandMark size={40} variant="loading" />
          <p className="text-white/60 text-xs">{t('preview.loadingTikTok')}</p>
        </div>
      </div>
    );
  }

  // Uploaded image
  if (parsed.type === 'image' && parsed.directUrl && !mediaError) {
    return (
      <div className={`${base} bg-black`}>
        <img src={parsed.directUrl} alt={t('preview.uploaded')} className="w-full h-full object-cover" onError={() => setMediaError(true)} />
        <Overlay />
      </div>
    );
  }

  // Uploaded video
  if (parsed.type === 'video' && parsed.directUrl && !mediaError) {
    if (isPlaying) {
      return (
        <div className={`${base} bg-black`}>
          <UploadedVideoPlayer src={parsed.directUrl} className="absolute inset-0 w-full h-full object-cover" autoPlay muted />
          <CoverSelector />
          <Overlay />
        </div>
      );
    }
    return (
      <div className={`${base} bg-black group cursor-pointer`} onClick={() => setIsPlaying(true)}>
        {coverPreviewUrl ? (
          <img src={coverPreviewUrl} alt={t('preview.selectedCoverFrame')} className="absolute inset-0 w-full h-full object-cover" />
        ) : thumbnailUrl ? (
          <img src={thumbnailUrl} alt={t('preview.videoThumbnail')} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" />
        )}
        <CoverSelector />
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="size-16 rounded-full bg-white/90 dark:bg-black/70 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
            <svg className="size-8 text-gray-800 dark:text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </div>
        <Overlay />
      </div>
    );
  }

  // Server-provided iframe embed (TikTok oEmbed)
  if (initialEmbedUrl) {
    return (
      <div className={`${base} bg-black`}>
        <iframe
          src={initialEmbedUrl}
          className="w-full h-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={t('analytics.tiktok.tiktokVideo')}
        />
      </div>
    );
  }

  // Extracted video playing
  if (extractedVideoUrl && isPlaying) {
    return (
      <div className={`${base} bg-black`}>
        <video
          key={extractedVideoUrl}
          src={extractedVideoUrl}
          className="absolute inset-0 w-full h-full object-cover"
          controls
          autoPlay
          playsInline
          muted
          preload="metadata"
          onError={() => setVideoError('Video playback failed')}
        />
        {videoError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-20 gap-3">
            <AlertTriangle className="size-6 text-red-400" />
            <p className="text-white text-sm font-medium">{t('preview.failedToPlay')}</p>
            <Button
              size="sm" variant="outline"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              onClick={() => { setVideoError(null); setIsPlaying(false); setTimeout(() => setIsPlaying(true), 100); }}
            >{t('analytics.common.retry')}</Button>
          </div>
        )}
        <Overlay />
      </div>
    );
  }

  // TikTok URL detected — thumbnail + play / extract
  if (parsed.type === 'tiktok' && parsed.directUrl) {
    return (
      <div className={`${base} bg-gradient-to-b from-gray-900 to-black`}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={t('analytics.tiktok.thumbnail')} className="absolute inset-0 w-full h-full object-cover" onError={() => setThumbnailUrl(null)} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-900/30 to-gray-900" />
        )}
        <div className="absolute inset-0 bg-black/35" />

        {/* Warning */}
        {videoError && !extractedVideoUrl && (
          <div className="absolute top-2 left-2 right-2 z-20 bg-amber-500/95 backdrop-blur-sm rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="size-4 text-white shrink-0 mt-0.5" />
            <p className="text-xs text-white font-medium leading-tight">{videoError}</p>
          </div>
        )}

        {isExtracting ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
            <BrandLoader label={t('common.loading')} />
            <span className="text-xs text-white/80 font-medium">{t('preview.extractingTikTok')}</span>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
            {/* Play button (extract on click if boardId available) */}
            {boardId ? (
              <button onClick={handlePlay} className="group/play flex flex-col items-center gap-3">
                <div className="size-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl group-hover/play:scale-110 transition-transform">
                  <Play className="size-8 text-gray-800 ml-1" fill="currentColor" />
                </div>
              </button>
            ) : (
              /* No boardId — open in TikTok */
              <a
                href={parsed.directUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm transition-colors"
              >
                <ExternalLink className="size-4" />{t('preview.openInTikTok')}</a>
            )}
          </div>
        )}

        <Overlay />
      </div>
    );
  }

  // Placeholder
  return (
    <div className={`${base} bg-gradient-to-b from-gray-900 to-black flex flex-col items-center justify-center gap-3 group cursor-pointer`}>
      <div className="size-16 rounded-2xl bg-gray-800 border border-white/10 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
        <SiTiktok className="size-8 text-white" />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-gray-300">{emptyLabel ?? (/tiktok\.com\/@[^/]+\/?$/.test(url ?? '') ? t('preview.tiktokPrivatePost') : t('preview.addTikTokUrl'))}</p>
        <p className="text-xs text-gray-500 mt-0.5">9:16 · TikTok format</p>
      </div>
      <Overlay />
    </div>
  );
}
